/* Theme-owned homepage behavior. Source slide counts, spacing and media remain in HTML/CSS. */
(() => {
  'use strict';
  const rootSelector = '[data-native-home-page]';
  const running = new WeakMap();
  const device = () => innerWidth < 767.5 ? 'mobile' : innerWidth < 1024.5 ? 'tablet' : innerWidth < 1199.5 ? 'laptop' : 'all';
  function initialize(root) {
    running.get(root)?.();
    const controller = new AbortController(), { signal } = controller;
    const cleanup = [];
    const lazy = root.dataset.nativeBgClass;
    const backgrounds = [...root.querySelectorAll('.' + lazy)];
    const reveal = element => element.classList.remove(lazy);
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) { reveal(entry.target); observer.unobserve(entry.target); }
      }), { rootMargin: '300px' });
      backgrounds.forEach(element => observer.observe(element));
      cleanup.push(() => observer.disconnect());
    } else {
      const timer = setTimeout(() => backgrounds.forEach(reveal), 3000);
      cleanup.push(() => clearTimeout(timer));
    }
    root.querySelectorAll('[data-native-href]').forEach(element => {
      function open(event) {
        const url = element.getAttribute('data-native-href');
        if (!url) return;
        event.stopPropagation();
        window.open(url, event.ctrlKey || event.metaKey ? '_blank' : element.getAttribute('data-target') || '_self');
      }
      element.addEventListener('click', open, { signal });
      element.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); open(event); } }, { signal });
    });
    root.querySelectorAll('[data-native-home-carousel]').forEach(owner => {
      const config = JSON.parse(owner.dataset.nativeHomeCarousel);
      const track = owner.querySelector('.np-home-content-c-1bad91143d8a');
      if (!track) return;
      const slides = [...track.querySelectorAll(':scope > .np-home-content-c-7dfc465d5a29')];
      const previous = track.querySelector(':scope > .np-home-content-c-60f6e57d79a3');
      const next = track.querySelector(':scope > .np-home-content-c-13701d1364fd');
      const pagination = track.querySelector(':scope > .np-home-content-c-61a4c9f8117e');
      let inView = false, hovering = false;
      let mode, current = 0, timer, scrollTimer, pendingTargetLeft = null, pendingManual = false, layoutWidth = null;
      const show = () => Number(config.slidesToShow[mode]);
      const step = () => Number(config.slidesToScroll[mode]);
      const last = () => Math.max(0, slides.length - show());
      function target(index) {
        const distance = slides[index].offsetLeft - slides[0].offsetLeft;
        const padding = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
        const pixels = getComputedStyle(track).scrollPaddingLeft.endsWith('%') ? track.clientWidth * padding / 100 : padding;
        return Math.min(Math.max(0, track.scrollWidth - track.clientWidth), Math.max(0, distance - pixels));
      }
      function update() {
        [...(pagination?.children || [])].forEach((button, index) => {
          const active = index === Math.min(Math.ceil(current / step()), pagination.children.length - 1);
          button.classList.toggle('np-home-content-c-96879611650f', active);
          button.setAttribute('aria-current', String(active));
        });
        [previous, next].forEach(button => { if (button) button.style.visibility = slides.length > show() ? 'visible' : 'hidden'; });
        if (previous) previous.disabled = !config.loop && current === 0;
        if (next) next.disabled = !config.loop && current >= last();
      }
      function releasePending() {
        pendingTargetLeft = null;
        pendingManual = false;
        clearTimeout(scrollTimer);
      }
      function go(index, manual = false) {
        clearTimeout(scrollTimer);
        current = Math.max(0, Math.min(last(), index));
        const left = target(current);
        pendingTargetLeft = Math.abs(track.scrollLeft - left) > 1 ? left : null;
        pendingManual = manual && pendingTargetLeft !== null;
        track.scrollTo({ left, behavior: 'smooth' });
        update(); measure();
      }
      function move(direction, manual = false) {
        go(direction > 0 ? current >= last() && config.loop ? 0 : current + step() : current === 0 && config.loop ? last() : current - step(), manual);
      }
      function autoplay() {
        clearInterval(timer);
        if (inView && !document.hidden && !hovering && config.autoPlay && slides.length > show()) timer = setInterval(() => {
          if (pendingTargetLeft !== null) {
            if (Math.abs(track.scrollLeft - pendingTargetLeft) > 1) return;
            const restartAfterArrival = pendingManual;
            pendingTargetLeft = null;
            pendingManual = false;
            if (restartAfterArrival) { autoplay(); return; }
          }
          move(1);
        }, Number(config.autoPlayDelay));
      }
      function measure() {
        if (track.clientWidth > 0) {
          track.style.height = '';
          // Source maxHeight=true keeps the natural tallest-card flex height.
          // maxHeight=false follows the content of the current visible cards.
          if (!config.maxHeight) {
            const visible = slides.slice(current, current + show());
            const maximum = Math.max(...visible.map(slide => (slide.firstElementChild || slide).getBoundingClientRect().height));
            if (maximum > 0) track.style.height = maximum + 'px';
          }
        }
      }
      function resize(event) {
        const nextMode = device(), nextWidth = track.clientWidth;
        // A height-only ResizeObserver callback from measure() must not cancel a scroll command.
        if (event?.type === 'resize' || mode !== nextMode || layoutWidth !== nextWidth) releasePending();
        layoutWidth = nextWidth;
        if (mode !== nextMode) {
          mode = nextMode; current = Math.min(current, last());
          if (pagination && config.paginationStyle !== 'none') {
            pagination.replaceChildren();
            for (let i = 0; i < Math.ceil(last() / step()) + 1; i++) {
              const button = document.createElement('button');
              button.type = 'button'; button.setAttribute('aria-label', `Go to slide ${i + 1}`);
              button.addEventListener('click', () => { go(i * step(), true); autoplay(); }, { signal });
              pagination.append(button);
            }
          }
          update(); autoplay();
        }
        measure();
      }
      previous?.addEventListener('click', () => { move(-1, true); autoplay(); }, { signal });
      next?.addEventListener('click', () => { move(1, true); autoplay(); }, { signal });
      track.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          if (pendingTargetLeft !== null) {
            if (Math.abs(track.scrollLeft - pendingTargetLeft) > 1) return;
            const restartAfterArrival = pendingManual;
            pendingTargetLeft = null;
            pendingManual = false;
            if (restartAfterArrival) autoplay();
          }
          current = Array.from({ length: last() + 1 }, (_, i) => i).reduce((best, i) => Math.abs(target(i) - track.scrollLeft) < Math.abs(target(best) - track.scrollLeft) ? i : best, 0);
          update(); measure();
        }, 100);
      }, { signal, passive: true });
      function userInterrupted(event) {
        if (['pointerdown', 'touchstart'].includes(event.type) && event.target?.closest?.('button')) return;
        releasePending();
        current = Array.from({ length: last() + 1 }, (_, i) => i).reduce((best, i) => Math.abs(target(i) - track.scrollLeft) < Math.abs(target(best) - track.scrollLeft) ? i : best, 0);
        update(); measure(); autoplay();
      }
      ['wheel', 'pointerdown', 'touchstart'].forEach(type => track.addEventListener(type, userInterrupted, { signal, passive: true }));
      track.addEventListener('keydown', event => {
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar'].includes(event.key)) userInterrupted(event);
      }, { signal, passive: true });
      if (config.pauseOnHover) {
        owner.addEventListener('mouseenter', () => { hovering = true; clearInterval(timer); }, { signal });
        owner.addEventListener('mouseleave', () => { hovering = false; autoplay(); }, { signal });
      }
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => { inView = entries[0].isIntersecting; autoplay(); });
        observer.observe(owner); cleanup.push(() => observer.disconnect());
      } else inView = true;
      document.addEventListener('visibilitychange', autoplay, { signal });
      owner.querySelectorAll('img').forEach(img => img.addEventListener('load', measure, { signal }));
      window.addEventListener('resize', resize, { signal });
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(resize); observer.observe(owner); cleanup.push(() => observer.disconnect());
      }
      cleanup.push(() => { clearInterval(timer); clearTimeout(scrollTimer); });
      resize();
    });
    running.set(root, () => { controller.abort(); cleanup.forEach(fn => fn()); });
  }
  function scan(scope) {
    if (scope.matches?.(rootSelector)) initialize(scope);
    scope.querySelectorAll(rootSelector).forEach(initialize);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll(rootSelector).forEach(root => running.get(root)?.()));
})();
